/**
 * A SOR-MÓD MAGJA — tiszta függvények, React nélkül.
 *
 * A 2.6/22 eldőlt: **optimista mentés, visszagörgetéssel.** Az `Enter`
 * azonnal továbblép, a mentés a háttérben megy; ha elbukik, a sor visszajön,
 * megjelölve. Ez a fájl azt a három műveletet tartja, amit a horognak
 * ÁLLAPOT NÉLKÜL kell tudnia — így teszthető anélkül, hogy egy komponenst
 * fel kellene húzni hozzá.
 *
 * Miért külön fájl, és nem a `useWorklist` belsejében: a 2.6/16 ugyanaz az
 * érv. A sor-mód a komponensszerkezet része, nem ráaggatott viselkedés — ha
 * az előellenőrzés és a visszagörgetés a horog törzsében ül, minden későbbi
 * képernyő, ami sor-módot akar, újra megírja őket.
 */

import type { DiscrepancyListItem } from '@/features/discrepancies/types';
import type { Worklist, WorklistItem } from './types';

/**
 * AMIT A KLIENS ELŐRE TUD — a 2.6/22 első kötelezettsége.
 *
 * A *determinisztikus* visszautasításokat a továbblépés ELŐTT kell elkapni,
 * mert ezek a gyakoriak. Enélkül a kurzor naponta többször ugrálna vissza, és
 * az rosszabb, mint a pesszimista várakozás.
 *
 * A szerveren a lefűzés `processingState = DATA_READY`-t követel
 * (`document-filing.service.ts`), és azt a bejelentés mezői adják meg:
 * `REQUIRED_FOR_DATA_READY` = számlaszám, partner adószáma, kelt, bruttó
 * (`document-field.service.ts`). Mind a négynek van megfelelője a hiánytétel
 * listasorában, tehát a felület ki tudja számolni ELŐRE, hogy a lefűzés
 * nekifutna-e a visszautasításnak.
 *
 * ⚠️ AMIT EZ A GYAKORLATBAN MÉR — és ez nem ugyanaz, mint amit a neve ígér.
 * A `NavInvoice` mind a négy oszlopa `NOT NULL`, a listasor mezői pedig
 * `invoice?.x ?? null` alakban készülnek (`reconciliation.service.ts`
 * `toListRow`). Külön-külön tehát EGYIK SEM hiányozhat: a négyes akkor és
 * csak akkor bukik el, ha a hiánytételhez EGYÁLTALÁN NINCS bejelentés — az
 * M2b bank-ág. A szerver arra a `„A hiánytételhez nem tartozik
 * NAV-bejelentés"` 409-et adja, nem `DATA_READY`-hiányt; az üzenet ezért ezt
 * mondja. A négyes mégis mezőnként áll, mert így marad igaz akkor is, amikor
 * a bank-ág megérkezik és a mezők külön-külön is hiányozhatnak.
 *
 * ⚠️ AZ `issueDate` MÁS OSZLOP, MINT AMIT A SZERVER ÍR. A listasoré a
 * `Discrepancy.detectedFor` (`toListRow`), a lefűzéskor viszont a
 * `NavInvoice.issueDate` kerül a bizonylatra. Ma egybeesnek — a `detectedFor`
 * a tétel születésekor az `invoice.issueDate`-ből íródik
 * (`reconciliation.service.ts`) —, de a `detectedFor` NULLÁZHATÓ, a
 * `NavInvoice.issueDate` nem. Egy `detectedFor` nélküli, bejelentéssel bíró
 * tételt a felület tévesen visszautasítana. Amikor a bank-ág megérkezik, a
 * listasornak a bejelentés SAJÁT keltét is ki kell adnia, és ennek a
 * függvénynek arra kell átállnia. *(Code review fogta meg, 09-03.)*
 *
 * ⚠️ A KIMENŐ ÁGON HÁROM MEZŐ, NEM NÉGY (#51). A partner adószáma kimenő
 * soron kimarad, mert a vevőnek nem feltétlenül VAN adószáma — a szerver
 * oldalán ez a `requiredForDataReady`. Az elágazás mindkét helyen ugyanaz, és
 * a `direction` a listasoron is rajta van.
 *
 * ⚠️ EZ A NÉGY MEZŐ A SZERVERREL KÖZÖS IGAZSÁG. Ha ott bővül a
 * `REQUIRED_FOR_DATA_READY`, itt is bővülnie kell — különben az `Enter`
 * továbblép egy olyan sorról, amit a szerver visszautasít, és a könyvelő a
 * visszagörgetésből tudja meg. A DRIFT-ŐR A BACKENDEN VAN
 * (`document-field.service.spec.ts`): egy ötödik kulcs ott bukik el, és
 * onnan mutat ide. A `rowMode.spec.ts` a kliens oldalát rögzíti, magában
 * NEM őrzi a driftet — a kettő EGYÜTT teszi.
 */
export type FilingBlock = 'missingFields';

export function blockFor(candidate: DiscrepancyListItem): FilingBlock | null {
  const required = [candidate.invoiceNumber, candidate.issueDate, candidate.amount];

  // ⚠️ KIMENŐ SORON A PARTNER ADÓSZÁMA NEM KÖTELEZŐ — és ez a szerver
  // `requiredForDataReady`-jének a tükre, nem külön felületi kedvezmény.
  // Kimenő számlán a partner a VEVŐ, a NAV pedig a vevő adószámát nem
  // kötelezően adja: magánszemély vevőnek nincs adószáma. A négyest ott is,
  // itt is megkövetelve az ilyen sor SOSEM lépne tovább, és a könyvelőnek
  // nem lenne mit beírnia.
  //
  // A `direction: null` a bejövő ággal EGY MEGÍTÉLÉS ALÁ esik: az a régi,
  // irány nélküli sor, és azokon a vevő adószáma strukturálisan megvan. A
  // szigorúbb ág a biztonságos alapértelmezés — legfeljebb egy fölösleges
  // visszautasítást jósol, nem engedi át az `Enter`-t egy soron, amit a
  // szerver eldob.
  if (candidate.direction !== 'OUTBOUND') {
    required.push(candidate.partnerTaxNumber);
  }

  return required.some((value) => value === null) ? 'missingFields' : null;
}

/**
 * AZ OPTIMISTA ELTÁVOLÍTÁS. A sor eltűnik, és a darabszám is csökken — a
 * fejléc („Elintézetlen · 12") különben egy pillanatra hazudna.
 *
 * Az `awaitingDataCount` CSAK AKKOR csökken, ha az eltávolított sor tényleg
 * adatra várt. Vakon csökkentve egy `awaitingData: false` sor lefűzése a
 * sávot rossz számra vinné, és a szám a következő betöltésig ott maradna.
 */
export function removeItem(list: Worklist, documentId: string): Worklist {
  const item = list.items.find((candidate) => candidate.id === documentId);
  if (!item) {
    return list;
  }

  return {
    items: list.items.filter((candidate) => candidate.id !== documentId),
    totals: {
      itemCount: Math.max(list.totals.itemCount - 1, 0),
      awaitingDataCount: Math.max(list.totals.awaitingDataCount - (item.awaitingData ? 1 : 0), 0),
    },
  };
}

/**
 * A VISSZAGÖRGETÉS — a sor a HELYÉRE jön vissza, nem a lista tetejére.
 *
 * A helyet nem megjegyzett indexből számoljuk, hanem ÚJRARENDEZÉSSEL: amíg a
 * sor a levegőben volt, más sorok is eltűnhettek (ez a 2.6/22 második
 * kötelezettsége — a bukás „ritkán sok sor egyszerre"), és egy eltett index
 * addigra másik sorra mutatna.
 *
 * ⚠️ A RENDEZÉS A SZERVERÉVEL KÖZÖS IGAZSÁG: beérkezés szerint nő, holtverseny
 * az `id` (`document-worklist.view.ts` → `buildWorklist`). Ha ott változik,
 * itt is változnia kell, különben a visszagörgetett sor más helyre kerül,
 * mint ahol egy frissítés után lenne — és a könyvelő azt hinné, hogy a
 * rendszer megkeverte a kupacot.
 */
export function restoreItem(list: Worklist, item: WorklistItem): Worklist {
  if (list.items.some((candidate) => candidate.id === item.id)) {
    return list;
  }

  const items = [...list.items, item].sort((left, right) => {
    if (left.createdAt !== right.createdAt) {
      return left.createdAt < right.createdAt ? -1 : 1;
    }
    return left.id < right.id ? -1 : 1;
  });

  return {
    items,
    totals: {
      itemCount: list.totals.itemCount + 1,
      awaitingDataCount: list.totals.awaitingDataCount + (item.awaitingData ? 1 : 0),
    },
  };
}

/**
 * KI KÖVETKEZIK, ha ezt a sort most elvesszük.
 *
 * A LISTA VÉGÉN AZ ELŐZŐRE lép, nem a semmibe: a kupac utolsó papírját
 * lefűzve a fókusz különben elveszne, és a könyvelőnek egérrel kellene
 * visszatalálnia. Üres kupacnál `null` — ott tényleg nincs hova.
 */
export function idAfterRemoving(order: string[], documentId: string): string | null {
  const at = order.indexOf(documentId);
  if (at === -1) {
    return null;
  }
  return order[at + 1] ?? order[at - 1] ?? null;
}
